import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { IMAGES, BAND } from "../lib/content";
import { scrollToId } from "../hooks/useLenis";

const ease = [0.16, 1, 0.3, 1];

const lineVariants = {
  hidden: { y: "115%" },
  show: (d = 0) => ({ y: "0%", transition: { duration: 1, ease, delay: d } }),
};
const fadeVariants = {
  hidden: { opacity: 0, y: 20 },
  show: (d = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.9, ease, delay: d } }),
};

const Line = ({ children, delay }) => (
  <span className="reveal-line">
    <motion.span className="block" variants={lineVariants} custom={delay}>
      {children}
    </motion.span>
  </span>
);

export default function Hero({ start }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.05, 0.48]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const anim = start ? "show" : "hidden";

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] w-full overflow-hidden"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform">
        <img src={IMAGES.hero} alt="Θεσσαλονίκη ηλιοβασίλεμα" className="h-full w-full object-cover kb-breathe" />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[var(--bg)]" />
      <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg)] via-transparent to-[var(--bg)]/20" />

      <motion.div
        style={{ y: textY }}
        initial="hidden"
        animate={anim}
        className="relative z-10 flex h-full flex-col justify-end px-6 md:px-12 pb-24 md:pb-28 max-w-[1500px] mx-auto [text-shadow:0_1px_18px_rgba(233,243,252,0.6)]"
      >
        <motion.p
          variants={fadeVariants}
          custom={0.5}
          className="font-mono text-[11px] md:text-xs tracking-[0.45em] text-[var(--fg)] font-medium mb-6"
        >
          {BAND.since} · {BAND.city}
        </motion.p>

        <h1 className="font-serif font-medium leading-[0.9] text-[var(--fg)] text-[clamp(2.6rem,11vw,11rem)] tracking-tight whitespace-nowrap drop-shadow-[0_2px_24px_rgba(233,243,252,0.65)]">
          <Line delay={0.1}>ΤΑΞΙΔΙΩΤΕΣ</Line>
        </h1>

        <div className="mt-8 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p
            variants={fadeVariants}
            custom={0.6}
            className="max-w-md font-body font-medium text-base md:text-lg text-[var(--fg)] leading-relaxed [text-shadow:0_1px_2px_rgba(233,243,252,0.95),0_2px_16px_rgba(233,243,252,0.9)]"
          >
            {BAND.tagline}. Ένα ταξίδι λατρείας — <span className="italic font-serif text-[var(--fg)]">"Travelers gr"</span>.
          </motion.p>

          <motion.button
            data-testid="hero-cta"
            variants={fadeVariants}
            custom={0.7}
            onClick={() => scrollToId("music")}
            className="group inline-flex items-center gap-4 self-start rounded-full border border-[var(--line-gold)] bg-[var(--surface)]/40 backdrop-blur-sm px-7 py-4 font-mono text-[11px] tracking-[0.3em] uppercase text-[var(--fg)] hover:bg-[var(--gold)] hover:text-[#1a1814] transition-colors duration-500"
          >
            <span className="h-2 w-2 rounded-full bg-[var(--gold)] group-hover:bg-[#1a1814] transition-colors duration-500" />
            Δες το βίντεο
          </motion.button>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: start ? 1 : 0 }}
        transition={{ delay: 0.9, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex flex-col items-center gap-3"
      >
        <span className="font-mono text-[10px] tracking-[0.3em] text-[var(--muted)] [writing-mode:vertical-rl]">
          SCROLL
        </span>
        <motion.span
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="h-10 w-px bg-gradient-to-b from-[var(--gold)] to-transparent"
        />
      </motion.div>
    </section>
  );
}
